import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../supabaseClient';
import {
  Box, Typography, Button, TextField, Grid, Select, MenuItem, FormControl, InputLabel,
  IconButton, Dialog, DialogTitle, DialogContent, Stepper, Step, StepLabel, StepContent,
  Alert, alpha, useTheme, InputAdornment, Paper, Stack, StepConnector, stepConnectorClasses, styled,
  Table, TableHead, TableBody, TableRow, TableCell, DialogActions
} from '@mui/material';
import {
  Check as CheckIcon, Info as InfoIcon, AttachMoney as AttachMoneyIcon,
  Search as SearchIcon, Close as CloseIcon, Label as LabelIcon, KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderLeftWidth: 3,
    borderRadius: 1,
    marginLeft: 11,
  },
}));

const QontoStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#eaeaf0',
  display: 'flex', height: 22, alignItems: 'center',
  ...(ownerState.active && { color: theme.palette.primary.main }),
  '& .QontoStepIcon-completedIcon': { color: theme.palette.primary.main, zIndex: 1, fontSize: 18 },
  '& .QontoStepIcon-circle': { width: 8, height: 8, borderRadius: '50%', backgroundColor: 'currentColor' },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;
  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? <CheckIcon className="QontoStepIcon-completedIcon" /> : <div className="QontoStepIcon-circle" />}
    </QontoStepIconRoot>
  );
}

export default function LabelsConfig({ open, onClose, editingItem, companyId, suppliers }) {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [selectedDialogSupplier, setSelectedDialogSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '', sku: '', unit: 'piece', width_mm: '', height_mm: '', dimensions_unit: 'mm',
    material: '', adhesive_type: '', finish: '', shape: '', special_features: '', description: '',
    supplier_id: '', unit_cost: '', moq: '', lead_time: '', notes: ''
  });

  useEffect(() => {
    if (open) {
      if (editingItem) {
        setFormData({
          name: editingItem.name || '', sku: editingItem.sku || '', unit: editingItem.unit || 'piece',
          width_mm: editingItem.width_mm || '', height_mm: editingItem.height_mm || '',
          dimensions_unit: editingItem.dimensions_unit || 'mm', material: editingItem.material || '',
          adhesive_type: editingItem.adhesive_type || '', finish: editingItem.finish || '',
          shape: editingItem.shape || '', special_features: editingItem.special_features || '',
          description: editingItem.description || '', supplier_id: editingItem.supplier_id || '',
          unit_cost: editingItem.unit_cost || '', moq: editingItem.moq || '', lead_time: editingItem.lead_time || '',
          notes: editingItem.notes || ''
        });
      } else {
        setFormData({
          name: '', sku: '', unit: 'piece', width_mm: '', height_mm: '', dimensions_unit: 'mm',
          material: '', adhesive_type: '', finish: '', shape: '', special_features: '', description: '',
          supplier_id: '', unit_cost: '', moq: '', lead_time: '', notes: ''
        });
      }
      setActiveStep(0);
      setError('');
    }
  }, [open, editingItem]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.name?.trim()) {
        setError('Label name is required');
        return;
      }
      if (!formData.sku?.trim()) {
        setError('SKU is required');
        return;
      }
      if (!formData.unit_cost) {
        setError('Unit cost is required');
        return;
      }

      // Check for duplicate SKU
      const { data: existingLabel, error: checkError } = await supabase
        .from('labels')
        .select('id')
        .eq('company_id', companyId)
        .eq('sku', formData.sku.trim())
        .neq('id', editingItem?.id || '00000000-0000-0000-0000-000000000000');

      if (checkError) {
        throw checkError;
      }
      if (existingLabel && existingLabel.length > 0) {
        setError('A label with this SKU already exists');
        return;
      }

      const payload = {
        company_id: companyId,
        ...formData,
        supplier_id: formData.supplier_id || null,
        width_mm: formData.width_mm ? parseFloat(formData.width_mm) : null,
        height_mm: formData.height_mm ? parseFloat(formData.height_mm) : null,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        moq: formData.moq ? parseInt(formData.moq) : null,
        lead_time: formData.lead_time ? parseInt(formData.lead_time) : null
      };
      if (editingItem?.id) {
        const { error } = await supabase.from('labels').update(payload).eq('id', editingItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('labels').insert([payload]);
        if (error) throw error;
      }
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOpenSupplierDialog = () => {
    setSupplierDialogOpen(true);
    setSupplierSearchTerm('');
    setSelectedDialogSupplier(formData.supplier_id ? suppliers.find(s => s.id === formData.supplier_id) : null);
  };

  const handleConfirmSupplier = () => {
    if (selectedDialogSupplier) {
      setFormData(prev => ({ ...prev, supplier_id: selectedDialogSupplier.id }));
    } else {
      setFormData(prev => ({ ...prev, supplier_id: '' }));
    }
    setSupplierDialogOpen(false);
  };

  const filteredSuppliers = supplierSearchTerm
    ? suppliers.filter(s => s.name.toLowerCase().includes(supplierSearchTerm.toLowerCase()))
    : suppliers;

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)',
      '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.3), borderWidth: 1 },
      '&:hover fieldset': { borderColor: theme.palette.primary.main },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
    },
    '& .MuiInputLabel-root': { fontWeight: 500 }
  };

  const selectSx = {
    borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)',
    '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.3), borderWidth: 1 },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`, backdropFilter: 'blur(20px)', boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}` } }}>
        <DialogTitle sx={{ p: 4, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>{editingItem ? 'Edit Label' : 'Create New Label'}</Typography>
          <IconButton onClick={onClose} sx={{ background: 'rgba(255, 255, 255, 0.8)', borderRadius: 2, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {error && <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>}
          <Box sx={{ px: 3, pt: 3 }}>
            <Stepper activeStep={activeStep} orientation="vertical" connector={<QontoConnector />}>
              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={{ '& .MuiStepLabel-label': { fontSize: '1.1rem', fontWeight: activeStep === 0 ? 500 : 400, color: activeStep === 0 ? 'primary.main' : 'text.secondary' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><InfoIcon sx={{ fontSize: 20 }} />Basic Information</Box>
                </StepLabel>
                <StepContent>
                  <Paper sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', mt: 2, mb: 3 }}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth label="Label Name *" value={formData.name} onChange={handleChange('name')} required sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField fullWidth label="SKU *" value={formData.sku} onChange={handleChange('sku')} required sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Unit</InputLabel>
                            <Select value={formData.unit} onChange={handleChange('unit')} label="Unit" sx={selectSx}>
                              <MenuItem value="piece">Piece</MenuItem>
                              <MenuItem value="roll">Roll</MenuItem>
                              <MenuItem value="sheet">Sheet</MenuItem>
                              <MenuItem value="box">Box</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label="Description" multiline rows={3} value={formData.description} onChange={handleChange('description')} sx={textFieldSx} />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!formData.name.trim() || !formData.sku.trim()} sx={{ borderRadius: 2 }}>Next: Specifications</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={{ '& .MuiStepLabel-label': { fontSize: '1.1rem', fontWeight: activeStep === 1 ? 500 : 400, color: activeStep === 1 ? 'primary.main' : 'text.secondary' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LabelIcon sx={{ fontSize: 20 }} />Specifications</Box>
                </StepLabel>
                <StepContent>
                  <Paper sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', mt: 2, mb: 3 }}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={5}>
                          <TextField fullWidth label="Width" type="number" value={formData.width_mm} onChange={handleChange('width_mm')} sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField fullWidth label="Height" type="number" value={formData.height_mm} onChange={handleChange('height_mm')} sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={2}>
                          <FormControl fullWidth>
                            <InputLabel>Unit</InputLabel>
                            <Select value={formData.dimensions_unit} onChange={handleChange('dimensions_unit')} label="Unit" sx={selectSx}>
                              <MenuItem value="mm">mm</MenuItem>
                              <MenuItem value="cm">cm</MenuItem>
                              <MenuItem value="in">in</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Material" value={formData.material} onChange={handleChange('material')} placeholder="e.g., Paper, Vinyl" sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Adhesive Type" value={formData.adhesive_type} onChange={handleChange('adhesive_type')} sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Finish" value={formData.finish} onChange={handleChange('finish')} placeholder="e.g., Glossy, Matte" sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Shape" value={formData.shape} onChange={handleChange('shape')} placeholder="e.g., Rectangle, Circle" sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label="Special Features" value={formData.special_features} onChange={handleChange('special_features')} placeholder="e.g., Waterproof, UV resistant" sx={textFieldSx} />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={() => setActiveStep(0)} sx={{ borderRadius: 2 }}>Back</Button>
                        <Button variant="contained" onClick={() => setActiveStep(2)} sx={{ borderRadius: 2 }}>Next: Business Details</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>

              <Step>
                <StepLabel StepIconComponent={QontoStepIcon} sx={{ '& .MuiStepLabel-label': { fontSize: '1.1rem', fontWeight: activeStep === 2 ? 500 : 400, color: activeStep === 2 ? 'primary.main' : 'text.secondary' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AttachMoneyIcon sx={{ fontSize: 20 }} />Business Details</Box>
                </StepLabel>
                <StepContent>
                  <Paper sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', mt: 2, mb: 3 }}>
                    <Box sx={{ p: 4 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                              Vendor
                            </Typography>
                            <Button fullWidth variant="outlined" onClick={handleOpenSupplierDialog}
                              sx={{ height: 56, borderRadius: 3, justifyContent: 'space-between', textTransform: 'none', backgroundColor: 'white', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, color: formData.supplier_id ? 'text.primary' : 'text.secondary', fontWeight: 500, px: 2, '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04), border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` } }}>
                              <Typography sx={{ fontSize: '1rem' }}>{formData.supplier_id ? suppliers.find(s => s.id === formData.supplier_id)?.name || 'Select Vendor' : 'Select Vendor'}</Typography>
                              <KeyboardArrowDownIcon sx={{ color: 'text.secondary' }} />
                            </Button>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Unit Cost" type="number" value={formData.unit_cost} onChange={handleChange('unit_cost')} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="MOQ" type="number" value={formData.moq} onChange={handleChange('moq')} sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Lead Time (days)" type="number" value={formData.lead_time} onChange={handleChange('lead_time')} sx={textFieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label="Notes" multiline rows={3} value={formData.notes} onChange={handleChange('notes')} placeholder="Additional notes or special instructions" sx={textFieldSx} />
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                        <Button onClick={() => setActiveStep(1)} sx={{ borderRadius: 2 }}>Back</Button>
                        <Button variant="contained" onClick={handleSubmit} sx={{ borderRadius: 2 }}>{editingItem ? 'Update Label' : 'Create Label'}</Button>
                      </Box>
                    </Box>
                  </Paper>
                </StepContent>
              </Step>
            </Stepper>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={supplierDialogOpen} onClose={() => setSupplierDialogOpen(false)} fullWidth maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 4, height: '80vh', maxHeight: '80vh', margin: 'auto', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, boxShadow: '0 24px 48px rgba(0,0,0,0.15)' } }}>
        <DialogTitle sx={{ pb: 2, fontSize: '1.25rem', fontWeight: 500, color: 'text.primary', borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>Select Vendor</Typography>
          <IconButton onClick={() => setSupplierDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField fullWidth placeholder="Search vendors..." value={supplierSearchTerm} onChange={(e) => setSupplierSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.palette.primary.main }} /></InputAdornment>,
                sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white', '&:hover': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` }, '&.Mui-focused': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}` } } }}
              size="medium" sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } } }} />
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Vendor</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers.map(supp => (
                  <TableRow key={supp.id} hover sx={{ cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' }, '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, py: 2, pl: 3, pr: 3 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>{supp.name}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined" onClick={() => setSelectedDialogSupplier(supp)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', px: 2, py: 1, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, bgcolor: selectedDialogSupplier?.id === supp.id ? 'black' : 'white', color: selectedDialogSupplier?.id === supp.id ? 'white' : 'text.primary', transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)', '&:hover': { border: `1px solid ${theme.palette.primary.main}`, transform: 'translateY(-1px)', boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`, bgcolor: selectedDialogSupplier?.id === supp.id ? 'black' : 'white', color: selectedDialogSupplier?.id === supp.id ? 'white' : 'text.primary' } }}>
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, background: alpha(theme.palette.grey[50], 0.5) }}>
          <Button onClick={() => setSupplierDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmSupplier} sx={{ borderRadius: 2, px: 3, bgcolor: 'black', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>Select Vendor</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
